/**
 * File Upload Service
 * 
 * This service handles all file upload operations using the privateApiClient.
 * It provides methods for both batch and individual file uploads.
 */

import { privateApiClient, handleApiError } from '../apiClient';
import API from '../../config/api';
import { validateFile, validateFiles } from '../../utils/fileValidation';

// Logger not required here; rely on higher-level services for logging

/**
 * Upload a single file to the server
 * @param {File} file - The file to upload
 * @param {String} documentType - Type of document (personal_photo, cv, security_clearance)
 * @param {String} nominationId - Optional nomination ID to associate with the file
 * @returns {Promise<Object>} - The uploaded file data
 */
export const uploadFile = async (file, documentType, nominationId = null) => {
  try {
    // Validate file before upload
    validateFile(file);
    
    // Create form data for the file
    const formData = new FormData();
    formData.append('file', file);
    
    if (documentType) {
      formData.append('document_type', documentType);
    }
    
    if (nominationId) {
      formData.append('nominationId', nominationId);
    }
    
    // Use the privateApiClient to upload the file
    const response = await privateApiClient.post(API.files.serverUpload, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // Return in the format expected by batchFileUploadService
    return {
      data: {
        id: response.data.id || response.data._id,
        url: response.data.url || response.data.fileUrl,
        filename: response.data.filename || response.data.name || file.name
      }
    };
  } catch (error) {
    return handleApiError(error, 'Failed to upload file');
  }
};

/**
 * Upload multiple files in a batch
 * @param {Array<File>} files - Array of files to upload
 * @param {String} nominationId - Optional nomination ID to associate with the files
 * @returns {Promise<Array>} - Array of uploaded file data
 */
export const uploadBatch = async (files, nominationId = null) => {
  try {
    // Validate all files before upload
    validateFiles(files);
    
    // Create form data for the files
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    
    if (nominationId) {
      formData.append('nominationId', nominationId);
    }
    
    // Use the privateApiClient to upload the files
    const response = await privateApiClient.post(API.files.batchUpload, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // Format the response to match what batchFileUploadService expects
    // It expects response.data to be an array of objects with id, url, and filename
    const formattedData = Array.isArray(response.data) ? 
      response.data.map((file, index) => ({
        id: file.id || file._id,
        url: file.url || file.fileUrl,
        filename: file.filename || file.name || files[index].name
      })) : 
      [];
    
    return { data: formattedData };
  } catch (error) {
    return handleApiError(error, 'Failed to upload files in batch');
  }
};

/**
 * Confirm a file upload (for S3 direct uploads)
 * @param {String} fileId - ID of the file to confirm
 * @returns {Promise<Object>} - The confirmed file data
 */
export const confirmUpload = async (fileId) => {
  try {
    const response = await privateApiClient.post(API.fileUpload.confirmUpload(fileId));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to confirm file upload');
  }
};

/**
 * Get a pre-signed URL for direct S3 upload
 * @param {String} nominationId - Nomination ID to associate with the file
 * @param {String} fileName - Name of the file
 * @param {String} fileType - MIME type of the file
 * @returns {Promise<Object>} - Object containing the upload URL and file ID
 */
export const getUploadUrl = async (nominationId, fileName, fileType) => {
  try {
    const response = await privateApiClient.post(
      API.fileUpload.requestPutUploadUrl(nominationId),
      { fileName, fileType }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to get upload URL');
  }
};

/**
 * Delete a file
 * @param {String} fileId - ID of the file to delete
 * @returns {Promise<Object>} - Response data
 */
export const deleteFile = async (fileId) => {
  try {
    const response = await privateApiClient.delete(API.files.delete(fileId));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to delete file');
  }
};

/**
 * Get files associated with a nomination
 * @param {String} nominationId - ID of the nomination
 * @returns {Promise<Array>} - Array of file data
 */
export const getFilesByNomination = async (nominationId) => {
  try {
    const response = await privateApiClient.get(API.files.getByNomination(nominationId));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to get files for nomination');
  }
};

// Export all functions as a single object
const fileUploadService = {
  uploadFile,
  uploadBatch,
  confirmUpload,
  getUploadUrl,
  deleteFile,
  getFilesByNomination
};

export default fileUploadService;
